<script setup lang="ts">
/**
 * #1457: open work nothing is working on, in one list of its own beside Blockers and Gates (CTO decision 3).
 * `GET /v1/waiting` decides what is on it: one row per issue/PR whose last run has ended, with no live or queued run,
 * no decision waiting, nothing on Blockers and no finish recorded. A finish is recorded by the run itself when it
 * ends on an outcome its task declares as finishing the work, or by a person here. A row stays however old it is.
 *
 * Each row says what the work is and links to it, the task that ran last, how that run ended, the reason it gave,
 * what the work waits on and how long it has waited. From a row a person can restart the work at a step, or mark it
 * finished. Both are the API's own actions, attributed to the person; neither touches the issue tracker.
 */
import { Button, ZhConfirmButton, ZhEmptyState, ZhPageTitle, ZhTooltip } from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, formatAgo, type TaskRow, type WaitingRow } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { waitingEndedLabel, waitingWorkLabel } from '@/lib/waiting'
import { reportHandledError } from '@/sentry'

/** A Waiting row as the table shows it. */
type WaitingTableRow = WaitingRow & {
    /** What the work is called: the issue or pull request key, or a work item's title. */
    work: string
    taskName: string
    ended: string
    waited: string
}

/** The sidebar badge counting these rows polls on 5s; the list it links to keeps the same beat. */
const REFRESH_MS = 5000

/**
 * Read the way Gates reads its queue: never while the tab is hidden, never a second read while one is in flight, and
 * a late answer never lands over a newer one. `now` ticks each second, so the wait keeps counting between reads.
 */
const {
    data: list,
    error: loadError,
    loading,
    now,
    updatedAt,
    reread,
} = useLiveResource(
    () => 'waiting',
    signal => api.waiting(signal),
    { everyMs: REFRESH_MS },
)
/** The enterprise's tasks, for the last task's name and the steps a restart can start at. Empty if unreadable. */
const tasks = ref<TaskRow[]>([])
/** Why the last action failed, until the next action or the next read. */
const actionError = ref<string | null>(null)
/**
 * Why the last read failed, in the API's own sentence where it said one (#1678). A refusal names the scope the list
 * needs and what the reader holds, which tells them who to ask. A bare status or code (`500`, `member_scope`) is
 * not a sentence, so it adds nothing to the fixed line.
 */
const loadErrorText = computed(() => {
    if (!loadError.value) {
        return null
    }
    const said = loadError.value.message.trim()
    return /\s/.test(said) ? `Could not load the Waiting list. ${said}` : 'Could not load the Waiting list.'
})
const error = computed(() => actionError.value ?? loadErrorText.value)
const notice = ref<string | null>(null)
/** The pipeline an action is in flight for, so its buttons wait. */
const busy = ref<string | null>(null)
/** The step each row's restart starts at, by pipeline: the last task's slug until someone picks another. */
const steps = ref<Record<string, string>>({})

// A read that lands clears a refusal shown from before it, as each read always has.
watch(updatedAt, () => {
    actionError.value = null
})

// A list that could not load is reported the way Home's blocks are (#1677), once each time it stops loading: a
// failure that lasts is one event, not one every poll, and the next failure after a good read is reported again.
watch(loadError, (failure, before) => {
    if (failure && !before) {
        reportHandledError(failure, 'Waiting list')
    }
})

const tasksById = computed(() => new Map(tasks.value.map(task => [task.id, task])))
/** The steps a restart is offered, by name. The API refuses one that is not on the work's pipeline, and says so. */
const stepOptions = computed(() => [...tasks.value].sort((a, b) => (a.name ?? a.slug).localeCompare(b.name ?? b.slug)))

const rows = computed<WaitingTableRow[]>(() =>
    (list.value?.rows ?? []).map(row => ({
        ...row,
        work: waitingWorkLabel(row),
        taskName: tasksById.value.get(row.taskId)?.name ?? tasksById.value.get(row.taskId)?.slug ?? row.taskId,
        ended: waitingEndedLabel(row.endedAs),
        waited: formatAgo(row.waitedMs + Math.max(0, now.value - (updatedAt.value ?? now.value))),
    })),
)

const table = useClientTable({
    data: rows,
    searchFields: ['pipeline', 'work', 'taskName', 'ended', 'reason', 'waitsOn'],
    defaultSort: 'since',
    defaultOrder: 'ASC',
})
/** #633: the table's search, sort and filters come back on the next visit. */
const listState = useListState('waiting', table.listFields)

/** Keyed off what the API returned, never the filtered rows, so a search that narrows to zero keeps the table. */
const empty = computed(() => !loading.value && (list.value?.rows.length ?? 0) === 0)

const columns: DataTableColumn[] = [
    { key: 'work', label: 'Work', sort: 'work', filterable: false },
    { key: 'taskName', label: 'Last task', sort: 'taskName', filterable: false },
    { key: 'ended', label: 'How it ended', sort: 'ended', type: 'text' },
    { key: 'reason', label: 'Reason', filterable: false, class: 'max-w-sm' },
    { key: 'waitsOn', label: 'Waits on', filterable: false },
    { key: 'waited', label: 'Waiting', sort: 'since', filterable: false },
    { key: 'actions', label: 'Actions', filterable: false, class: 'text-right' },
]

onMounted(() => {
    void loadTasks()
})

/** Names only: a page that cannot read tasks still lists the work, by task id, and restarts at the last task. */
async function loadTasks(): Promise<void> {
    tasks.value = (await api.tasks({ limit: 200 }).catch(() => null))?.data ?? []
}

/** The step a row's restart starts at: the one picked, else the last task's own slug. Empty when it is not known. */
function stepFor(row: WaitingRow): string {
    return steps.value[row.pipeline] ?? tasksById.value.get(row.taskId)?.slug ?? ''
}

function pickStep(row: WaitingRow, event: Event): void {
    steps.value = { ...steps.value, [row.pipeline]: (event.target as HTMLSelectElement).value }
}

/**
 * Runs one row's action, then reads the list again at once. A read started before the action is dropped rather than
 * waited on (`reread`), so its older answer cannot bring back a row the action has just taken off.
 */
async function act(pipeline: string, work: () => Promise<string | null>, failure: string): Promise<void> {
    busy.value = pipeline
    notice.value = null
    actionError.value = null
    try {
        notice.value = await work()
        await reread()
    } catch (caught) {
        actionError.value = caught instanceof Error ? `${failure} (${caught.message}).` : `${failure}.`
    } finally {
        busy.value = null
    }
}

/** Starts the work again at the chosen step. The new run is live, so the row leaves the list on the reload. */
async function restart(row: WaitingRow): Promise<void> {
    // #1796: a restart starts a run, so without an AI account nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    const step = stepFor(row)
    return act(
        row.pipeline,
        async () => {
            const run = await api.restartWaiting(row.pipeline, step)
            return run.status === 'pending'
                ? `Restart of ${row.pipeline} is queued: it starts when the member is free.`
                : `Restarted ${row.pipeline}.`
        },
        `Could not restart ${row.pipeline}`,
    )
}

/** Records the work as finished. OS-side only: the issue or pull request itself is not closed. */
function finish(row: WaitingRow): Promise<void> {
    return act(
        row.pipeline,
        async () => {
            await api.finishWaiting(row.pipeline)
            return `Marked ${row.pipeline} finished.`
        },
        `Could not mark ${row.pipeline} finished`,
    )
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>Waiting</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Open work nothing is working on: its last run has ended without finishing the work, nothing is live or
			queued, no decision is waiting and it is not on
			<RouterLink class="underline" to="/blockers">Blockers</RouterLink> or
			<RouterLink class="underline" to="/gates">Gates</RouterLink>. It stays here however long it waits, until
			someone restarts it or marks it finished. Work whose last run ended on an outcome its task declares as
			finishing the work is never listed, and nor is work whose last run a person cancelled. Restart starts
			the work again at the step you pick. Mark finished takes it off this list only: the issue or pull
			request itself is not closed.
		</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="waiting-error">{{ error }}</p>
		<p v-if="notice && !error" class="text-muted-foreground text-sm" data-testid="waiting-notice">{{ notice }}</p>
		<ZhEmptyState v-if="empty && !error">
			Nothing is waiting. Open work whose last run ended with nothing after it will show up here.
			<template #action>
				<Button as-child size="sm"><RouterLink to="/tasks">Go to Tasks</RouterLink></Button>
			</template>
		</ZhEmptyState>
		<DataTable
			v-else
			storage-key="waiting"
			:columns="columns"
			:loading="loading"
			:stacked-on-mobile="true"
			:list-state="listState"
			v-bind="table.tableProps.value"
		>
			<template #cell-work="{ row, value }">
				<!-- A work item has no page outside the OS yet: its title, marked as an item of the team's list. -->
				<span v-if="row.workItem" class="font-medium" data-testid="waiting-work-item">
					{{ value }}
					<span class="ml-1 text-xs text-muted-foreground">work item</span>
				</span>
				<!-- An issue or pull request by its key: the OS knows no git host's address to link it to (#589). -->
				<span v-else class="font-medium" data-testid="waiting-work">{{ value }}</span>
			</template>
			<template #cell-taskName="{ row, value }">
				<!-- The run that ended last, under the name of the task that ran it. -->
				<RouterLink class="underline" :to="`/runs/${row.runId}`" data-testid="waiting-run-link">{{ value }}</RouterLink>
			</template>
			<template #cell-reason="{ value }">
				<ZhTooltip v-if="value" :text="String(value)">
					<span class="line-clamp-2 block max-w-md whitespace-pre-line">{{ value }}</span>
				</ZhTooltip>
				<span v-else class="text-muted-foreground">No reason given</span>
			</template>
			<template #cell-waitsOn="{ value }">
				<span v-if="value">{{ value }}</span>
				<span v-else class="text-muted-foreground">Nothing named</span>
			</template>
			<template #cell-waited="{ row, value }">
				<ZhTooltip :text="`Ended ${new Date(String(row.since)).toLocaleString()}`">
					<span class="tabular-nums">{{ value }}</span>
				</ZhTooltip>
			</template>
			<template #cell-actions="{ row }">
				<span class="flex flex-wrap items-center justify-end gap-2">
					<select
						:value="stepFor(row as unknown as WaitingRow)"
						:aria-label="`Step to restart ${row.pipeline} at`"
						class="border-input bg-background rounded-md border px-2 py-1 text-sm"
						data-testid="waiting-step"
						:disabled="busy === row.pipeline || stepOptions.length === 0"
						@click.stop
						@change="pickStep(row as unknown as WaitingRow, $event)"
					>
						<option v-for="task in stepOptions" :key="task.id" :value="task.slug">{{ task.name ?? task.slug }}</option>
					</select>
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="waiting-restart"
						:disabled="busy === row.pipeline || !stepFor(row as unknown as WaitingRow)"
						@click.stop="restart(row as unknown as WaitingRow)"
					>
						Restart
					</Button>
					<ZhConfirmButton
						label="Mark finished"
						confirm-label="Mark finished"
						:title="`Mark ${row.pipeline} finished?`"
						description="It leaves the Waiting list and Executions files it as complete. The issue or pull request itself is not closed."
						:disabled="busy === row.pipeline"
						data-testid="waiting-finish"
						@confirm="finish(row as unknown as WaitingRow)"
					/>
				</span>
			</template>
		</DataTable>
	</section>
</template>
